// src/components/agent/parts/NoticeRow.js
//
// System notices (frontend spec §8.6 / §3.4): centred, muted, small —
// visually neither user nor assistant. Covers `notice` doc parts (icon by
// noticeKind), the interrupted-turn row, the compaction divider, and a concise
// paused fallback notice. The paused row is only a status hint: the one-off
// decision control is the ApprovalCard, which renders in place of this row
// whenever the parked cycle's approval can be shown.
import React from 'react';
import { Box, Chip, Typography, Divider, Tooltip } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CompressIcon from '@mui/icons-material/Compress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

import { toolLabel } from './toolLabels';

const KIND_ICONS = {
  sandbox_reset: RestartAltIcon,
  job_completion: TaskAltIcon,
  budget: WarningAmberIcon,
  interrupted: InfoOutlinedIcon,
  paused: PauseCircleOutlineIcon,
};

// The idempotent duplicate-send outcomes (email spec §3.5) are SUCCESSFUL tool
// results: the requested safety outcome — not submitting the same email twice —
// was achieved, so the backend returns them as ordinary tool information rather
// than an error. They are matched explicitly here so they always render as
// information with a completed tick, never with a warning or error treatment.
const DUPLICATE_SEND_MESSAGES = new Set([
  'This email was already processed and was not submitted again.',
  'A previous submission already exists and may have been accepted. It was not submitted again.',
]);

export const isDuplicateSendMessage = (text) => DUPLICATE_SEND_MESSAGES.has(String(text || '').trim());

const OUTCOME_LABELS = { SUCCEEDED: 'completed', FAILED: 'failed', CANCELLED: 'cancelled' };

// A backend delivery notification is a header line followed by one bullet per
// finished job:
//   "Background jobs from your earlier request are complete:
//    • job_abc (execute_bash: sleep 5; ...) — SUCCEEDED, exit 0 — ...
//    • job_def (get_bigquery_data: Premiums) — FAILED: timed out ..."
// A bullet can span several lines, because the command or task it quotes is
// embedded verbatim, newlines included. The notice is therefore split on
// bullet markers rather than on line breaks, and each bullet is read as a
// whole (B9-9).
const BULLET_START = /^•\s+\S+\s+\(([a-z0-9_]+):/;
// The outcome follows the descriptor's closing parenthesis: ") — SUCCEEDED, …",
// ") — FAILED, …", ") — CANCELLED…". The descriptor quotes the job's command
// or task verbatim, which can itself contain a marker, so the last match in
// the bullet is the real one: the command comes before the outcome.
const OUTCOME = /\)\s*—\s*(SUCCEEDED|FAILED|CANCELLED)\b/g;

// One entry per bullet, or null when the text holds no bullets or one of them
// cannot be read, so the caller shows the whole notice instead of dropping the
// part it could not parse (smoke notices, future wordings).
const parseJobBullets = (text) => {
  const chunks = String(text || '').split(/\n(?=•\s)/);
  const bullets = chunks.filter((chunk) => chunk.startsWith('•'));
  if (bullets.length === 0) return null;
  const jobs = [];
  for (const bullet of bullets) {
    const match = bullet.match(BULLET_START);
    if (!match) return null;
    const outcomes = [...bullet.matchAll(OUTCOME)];
    if (outcomes.length === 0) return null;
    jobs.push({
      tool: match[1],
      text: bullet,
      outcome: outcomes[outcomes.length - 1][1],
    });
  }
  return jobs;
};

/**
 * kind: 'sandbox_reset' | 'job_completion' | 'budget' | 'interrupted' |
 *       'paused' | 'compaction' | 'turn_recovery' — otherwise use the info icon.
 */
function NoticeRow({ kind, text }) {
  if (kind === 'compaction') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 1.5, px: 2 }}>
        <Divider sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.disabled' }}>
          <CompressIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
            {text || 'Conversation summarised'}
          </Typography>
        </Box>
        <Divider sx={{ flex: 1 }} />
      </Box>
    );
  }

  if (kind === 'job_completion') {
    const jobs = parseJobBullets(text);
    if (jobs) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            my: 1,
            px: 2,
          }}
        >
          {/* The chip carries the whole bullet as its tooltip: for a failed
              job that is the only place the reason and the log path show. */}
          {jobs.map((job, i) => (
            <Tooltip key={i} title={job.text} componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-wrap' } } }}>
              <Chip
                size="small"
                variant="outlined"
                icon={
                  job.outcome === 'CANCELLED'
                    ? <CancelOutlinedIcon sx={{ fontSize: 15 }} />
                    : job.outcome === 'FAILED'
                      ? <ErrorOutlineIcon sx={{ fontSize: 15, color: 'error.main !important' }} />
                      : <TaskAltIcon sx={{ fontSize: 15, color: 'success.main !important' }} />
                }
                label={`Background job ${OUTCOME_LABELS[job.outcome]}: ${toolLabel(job.tool)}`}
                sx={{ color: 'text.secondary', borderColor: 'divider' }}
              />
            </Tooltip>
          ))}
        </Box>
      );
    }
  }

  const Icon = isDuplicateSendMessage(text)
    ? TaskAltIcon
    : (KIND_ICONS[kind] || InfoOutlinedIcon);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        my: 1,
        px: 2,
        color: 'text.disabled',
      }}
    >
      <Icon sx={{ fontSize: 15 }} />
      <Typography variant="caption" sx={{ textAlign: 'center', whiteSpace: 'pre-wrap' }}>
        {kind === 'turn_recovery' ? 'Resuming saved work' : text}
      </Typography>
    </Box>
  );
}

export default NoticeRow;
